import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface ImageHoverViewerAnchor {
  src?: string;
  name?: string;
  variant?: string;
  rect: DOMRect;
}

/**
 * Hover-preview state shared by:
 *   - `ImageHoverPreviewDirective` — pushes the anchor on mouseenter, clears on mouseleave.
 *   - `ImageHoverViewerComponent` — reads the anchor and renders the popup.
 *
 * IMPORTANT — not `providedIn: 'root'`. It must be provided by an owning host
 * component (e.g. `SalesOrderFormNewLayoutComponent.providers`) so that:
 *   1. `ngOnDestroy` actually fires (root-scoped services live forever and never
 *      destroy, which would leak the window listeners we register below);
 *   2. Directives and the viewer mounted inside that host's template resolve the
 *      same instance via Angular's hierarchical DI.
 *
 * Auto-hides on window scroll/resize so the popup never sits at a stale viewport
 * position when the underlying element moves. Listeners run outside Angular's zone
 * to avoid triggering full change detection while idle.
 */
@Injectable()
export class ImageHoverPreviewService implements OnDestroy {

  private readonly anchor$$ = new BehaviorSubject<ImageHoverViewerAnchor | null>(null);
  readonly anchor$: Observable<ImageHoverViewerAnchor | null> = this.anchor$$.asObservable();

  private readonly dismiss = () => {
    if (this.anchor$$.value !== null) {
      // Re-enter Angular's zone so the bound viewer template updates.
      this.zone.run(() => this.anchor$$.next(null));
    }
  };

  constructor(private zone: NgZone) {
    this.zone.runOutsideAngular(() => {
      window.addEventListener('scroll', this.dismiss, { capture: true, passive: true });
      window.addEventListener('resize', this.dismiss, { passive: true });
    });
  }

  show(anchor: ImageHoverViewerAnchor): void {
    this.anchor$$.next(anchor);
  }

  hide(): void {
    this.dismiss();
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.dismiss, { capture: true });
    window.removeEventListener('resize', this.dismiss);
    this.anchor$$.complete();
  }
}
